// src/components/website-preview/PoliciesPage.js
import React, { useEffect } from 'react';
import { hexToRgba } from '../../utils/colorUtils';
import { sanitizeHtml } from '../../utils/sanitize';

const PoliciesPage = ({
  privacyPolicy,
  termsOfService,
  cancellationPolicy,
  onBack,
  colors,
  fonts,
  embedded,
  scrollTarget,
}) => {
  // Helper functions for cancellation policy formatting
  const getTimeframeText = (policy) => {
    const map = {
      '24h': '24 hours',
      '48h': '48 hours',
      '72h': '72 hours',
      '1week': 'one week',
      'custom': policy.customTimeframe || 'the required timeframe',
    };
    return map[policy.timeframe] || '24 hours';
  };

  const getRefundText = (policy) => {
    const map = {
      'full': 'full refund',
      '50': '50% refund',
      'none': 'no refund',
      'custom': policy.customRefund || 'applicable refund',
    };
    return map[policy.refundPercentage] || 'full refund';
  };

  // Scroll to target section after mount
  useEffect(() => {
    if (scrollTarget) {
      setTimeout(() => {
        const element = document.getElementById(scrollTarget);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [scrollTarget]);

  // Check if any policies exist
  const hasPolicies = privacyPolicy?.content || termsOfService?.content || cancellationPolicy;

  // Build array of existing policies (for divider logic)
  const existingPolicies = [];
  if (privacyPolicy?.content) existingPolicies.push('privacy');
  if (termsOfService?.content) existingPolicies.push('terms');
  if (cancellationPolicy) existingPolicies.push('cancellation');

  return (
    <div
      id="policies-page"
      style={{
        maxWidth: '720px',
        margin: '0 auto',
        padding: '40px 24px',
        backgroundColor: colors?.background || '#FFFFFF',
        minHeight: '60vh',
      }}
    >
      {/* Back to site link */}
      <button
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '14px',
          color: colors?.primary || '#7c3aed',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px 0',
          fontWeight: '500',
        }}
      >
        ← Back to site
      </button>

      {/* Page title */}
      <h1
        style={{
          fontFamily: fonts?.heading || 'Playfair Display, serif',
          fontSize: '32px',
          fontWeight: '700',
          textAlign: 'center',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: colors?.text || '#1F2937',
          margin: '24px 0',
        }}
      >
        Policies
      </h1>

      {/* Legal disclaimer */}
      <div
        style={{
          padding: '12px 16px',
          backgroundColor: '#F9FAFB',
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          marginBottom: '32px',
          textAlign: 'center',
        }}
      >
        <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.5', margin: 0 }}>
          These policies were generated using templates and are provided for informational purposes only.
          We recommend consulting a qualified attorney for legal advice specific to your situation.
        </p>
      </div>

      {/* No policies message */}
      {!hasPolicies && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' }}>
          <p style={{ fontSize: '16px', margin: 0 }}>No policies have been created yet.</p>
        </div>
      )}

      {/* Privacy Policy */}
      {privacyPolicy?.content && (
        <>
          <section id="privacy-policy" style={{ marginBottom: '48px' }}>
            <div
              style={{
                fontFamily: fonts?.body || 'Montserrat, sans-serif',
                fontSize: '14px',
                lineHeight: '1.7',
                color: colors?.text || '#374151',
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(privacyPolicy.content) }}
            />
          </section>
          {existingPolicies.indexOf('privacy') < existingPolicies.length - 1 && (
            <div
              style={{
                height: '1px',
                backgroundColor: colors?.text ? hexToRgba(colors.text, 0.1) : '#E5E7EB',
                margin: '40px 0',
              }}
            />
          )}
        </>
      )}

      {/* Terms of Service */}
      {termsOfService?.content && (
        <>
          <section id="terms-of-service" style={{ marginBottom: '48px' }}>
            <div
              style={{
                fontFamily: fonts?.body || 'Montserrat, sans-serif',
                fontSize: '14px',
                lineHeight: '1.7',
                color: colors?.text || '#374151',
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(termsOfService.content) }}
            />
          </section>
          {existingPolicies.indexOf('terms') < existingPolicies.length - 1 && (
            <div
              style={{
                height: '1px',
                backgroundColor: colors?.text ? hexToRgba(colors.text, 0.1) : '#E5E7EB',
                margin: '40px 0',
              }}
            />
          )}
        </>
      )}

      {/* Cancellation Policy */}
      {cancellationPolicy && (
        <section id="cancellation-policy" style={{ marginBottom: '48px' }}>
          <h2
            style={{
              fontFamily: fonts?.heading || 'Playfair Display, serif',
              fontSize: '24px',
              fontWeight: '600',
              margin: '0 0 16px',
              color: colors?.text || '#1F2937',
            }}
          >
            Cancellation Policy
          </h2>
          <div
            style={{
              fontFamily: fonts?.body || 'Montserrat, sans-serif',
              fontSize: '14px',
              lineHeight: '1.7',
              color: colors?.text || '#374151',
            }}
          >
            <p style={{ margin: '8px 0' }}>
              If you need to cancel your appointment, please do so at least{' '}
              <strong>{getTimeframeText(cancellationPolicy)}</strong> in advance to receive a{' '}
              <strong>{getRefundText(cancellationPolicy)}</strong>.
            </p>
            <p style={{ margin: '8px 0' }}>
              Cancellations made within {getTimeframeText(cancellationPolicy)} of your scheduled
              appointment may not be eligible for a refund.
            </p>
            {cancellationPolicy.noShowPolicy && (
              <>
                <h3
                  style={{
                    fontFamily: fonts?.heading || 'Playfair Display, serif',
                    fontSize: '18px',
                    fontWeight: '600',
                    margin: '24px 0 12px',
                    color: colors?.text || '#1F2937',
                  }}
                >
                  No-Show Policy
                </h3>
                <p style={{ margin: '8px 0' }}>{cancellationPolicy.noShowPolicy}</p>
              </>
            )}
          </div>
        </section>
      )}

      {/* Scoped CSS for rendered HTML */}
      <style>{`
        #policies-page h2 {
          font-family: ${fonts?.heading || 'Playfair Display, serif'};
          font-size: 24px;
          font-weight: 600;
          margin: 32px 0 16px;
          color: ${colors?.text || '#1F2937'};
        }
        #policies-page h3 {
          font-family: ${fonts?.heading || 'Playfair Display, serif'};
          font-size: 18px;
          font-weight: 600;
          margin: 24px 0 12px;
          color: ${colors?.text || '#1F2937'};
        }
        #policies-page p {
          margin: 8px 0;
          line-height: 1.7;
        }
        #policies-page ul,
        #policies-page ol {
          padding-left: 24px;
          margin: 8px 0;
        }
        #policies-page li {
          margin: 4px 0;
        }
        #policies-page em {
          font-style: italic;
          color: ${colors?.secondaryText || '#6B7280'};
        }
        #policies-page strong {
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default PoliciesPage;
